import BaseTrackingService from '../BaseTrackingService';
import { handleAxiosErrorMessage } from '../customErrors/apiErrors';
import { EVENTS } from './trackEvents';

class PaymentLinkTrackingService extends BaseTrackingService {
  static EVENT_PAYMENT_LINK_VALIDATED = EVENTS.PAYMENT_LINK_VALIDATED;
  static EVENT_PAYMENT_LINK_EXPIRED = EVENTS.PAYMENT_LINK_EXPIRED;
  static EVENT_PAYMENT_LINK_ALREADY_PAID = EVENTS.PAYMENT_LINK_ALREADY_PAID;
  static EVENT_PAYMENT_LINK_STARTED = EVENTS.PAYMENT_LINK_STARTED;
  static EVENT_PAYMENT_LINK_CARD_TOKENIZATION_SUCCESS =
    EVENTS.PAYMENT_LINK_CARD_TOKENIZATION_SUCCESS;
  static EVENT_PAYMENT_LINK_CARD_TOKENIZATION_ERROR =
    EVENTS.PAYMENT_LINK_CARD_TOKENIZATION_ERROR;
  static EVENT_PAYMENT_LINK_SUBMITTED =
    EVENTS.PAYMENT_LINK_SUBMITTED_BUTTON_CLICKED;
  static EVENT_PAYMENT_LINK_POLLING_STARTED =
    EVENTS.PAYMENT_LINK_POLLING_STARTED;
  static EVENT_PAYMENT_LINK_POLLING_SUCCESS =
    EVENTS.PAYMENT_LINK_PAYMENT_SUCCESS;
  static EVENT_PAYMENT_LINK_POLLING_FAILED = EVENTS.PAYMENT_LINK_PAYMENT_FAILED;
  static EVENT_PAYMENT_LINK_POLLING_TIMEOUT =
    EVENTS.PAYMENT_LINK_POLLING_TIMEOUT;
  static EVENT_PAYMENT_LINK_SUBMISSION_FAILED =
    EVENTS.PAYMENT_LINK_SUBMISSION_FAILED;

  constructor() {
    super('PaymentLinkTrackingService');
  }

  /**
   * Monta propriedades para `trackPaymentLinkValidated` em trackEvents.ts.
   */
  preparePaymentLinkValidatedForAnalytics(linkData, planData) {
    return {
      link_id: linkData.link,
      plan_id: planData?.id,
      plan_name: planData?.name,
      plan_amount: planData?.amount,
      plan_days: planData?.days,
      allowed_payment_methods: linkData.allowedPaymentMethods,
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_VALIDATED,
      status: 'success',
      loaded_at: new Date().toISOString(),
      original_timestamp: new Date().toISOString(),
      provider: 'pagarme_v5',
      provider_id: linkData.plan.provider_id,
      trial_days: linkData.plan.trial_days,
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkExpired` em trackEvents.ts.
   */
  preparePaymentLinkExpiredForAnalytics(linkData) {
    return {
      link_id: linkData.link,
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_EXPIRED,
      status: 'expired',
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkAlreadyPaid` em trackEvents.ts.
   */
  preparePaymentLinkAlreadyPaidForAnalytics(linkData) {
    return {
      link_id: linkData.link,
      paid_at: linkData.paid_at,
      submitted_at: linkData.submitted_at,
      plan_name: linkData.plan.name,
      provider_id: linkData.plan.provider_id,
      plan_days: linkData.plan?.days,
      provider: 'pagarme_v5',
      amount: linkData.plan.amount,
      trial_days: linkData.plan.trial_days,
      allowed_payment_methods: linkData.allowedPaymentMethods,
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_ALREADY_PAID,
      status: 'already_paid',
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkStarted` em trackEvents.ts.
   */
  preparePaymentLinkStartedForAnalytics(linkData, planData, paymentMethod) {
    return {
      link_id: linkData.link,
      allowed_payment_methods: linkData.allowedPaymentMethods,
      plan_id: planData?.id,
      plan_name: planData?.name,
      plan_amount: planData?.amount,
      plan_days: planData?.days,
      payment_method: paymentMethod,
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_STARTED,
      status: 'started',
      provider: 'pagarme_v5',
      provider_id: linkData.plan.provider_id,
      trial_days: linkData.plan.trial_days,
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkCardTokenizationSuccess` em trackEvents.ts.
   */
  preparePaymentLinkCardTokenizationSuccessForAnalytics(
    linkData,
    planData,
    tokenData,
    cardData
  ) {
    return {
      link_id: linkData.link,
      plan_id: planData?.id,
      plan_name: planData?.name,
      plan_amount: planData?.amount,
      token_id: tokenData.id,
      card_brand: tokenData.card.brand || 'unknown',
      card_number_masked: tokenData.card.last_four_digits || '****',
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_CARD_TOKENIZATION_SUCCESS,
      status: 'success',
      loaded_at: new Date().toISOString(),
      original_timestamp: new Date().toISOString(),
      provider: linkData?.plan?.provider_id,
      trial_days: linkData?.plan?.trial_days,
      allowed_payment_methods: linkData.allowedPaymentMethods,
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkCardTokenizationError` em trackEvents.ts.
   */
  preparePaymentLinkCardTokenizationErrorForAnalytics(
    linkData,
    planData,
    error,
    cardData
  ) {
    return {
      link_id: linkData.link,
      allowed_payment_methods: linkData.allowedPaymentMethods,
      plan_id: planData?.id,
      plan_name: planData?.name,
      plan_amount: planData?.amount,
      error_type: handleAxiosErrorMessage(error).error_type || 'polling_failed',
      error_message:
        handleAxiosErrorMessage(error).message ||
        'Falha no polling da assinatura',
      error_code: handleAxiosErrorMessage(error).status || 'unknown',
      error_data: handleAxiosErrorMessage(error).data,
      card_brand: cardData.card_brand || 'unknown',
      card_number_masked: cardData.card_number_masked || '****',
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_CARD_TOKENIZATION_ERROR,
      status: 'error',
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkSubmittedButtonClicked` em trackEvents.ts.
   */
  preparePaymentLinkSubmittedForAnalytics(linkData, planData, tokenData) {
    return {
      allowed_payment_methods: linkData.allowedPaymentMethods,
      link_id: linkData.link,
      plan_id: planData?.id,
      plan_name: planData?.name,
      plan_amount: planData?.amount,
      token_id: tokenData.id,
      card_brand: tokenData.card.brand || 'unknown',
      card_number_masked: tokenData.card.last_four_digits || '****',
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_SUBMITTED,
      status: 'submitted',
      provider: 'pagarme_v5',
      provider_id: linkData.plan.provider_id,
      trial_days: linkData.plan.trial_days,
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkPollingStarted` em trackEvents.ts.
   */
  preparePaymentLinkPollingStartedForAnalytics(
    linkData,
    planData,
    subscriptionId,
    tokenData
  ) {
    return {
      link_id: linkData.link,
      plan_id: planData?.id,
      plan_name: planData?.name,
      plan_amount: planData?.amount,
      subscription_id: subscriptionId,
      ...(tokenData ? {
        token_id: tokenData.id,
        card_brand: tokenData?.card?.brand || 'unknown',
        card_number_masked: tokenData?.card?.last_four_digits || '****',
      } : {}),
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_POLLING_STARTED,
      status: 'polling_started',
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkPaymentSuccess` em trackEvents.ts.
   */
  preparePaymentLinkPollingSuccessForAnalytics(
    linkData,
    planData,
    subscriptionData,
    tokenData
  ) {
    return {
      link_id: linkData.link,
      plan_id: planData?.id,
      plan_name: planData?.name,
      plan_amount: planData?.amount,
      subscription_id: subscriptionData.subscriptionId,
      subscription_status: subscriptionData.status,
      ...(tokenData ? {
        token_id: tokenData.id,
        card_brand: tokenData?.card?.brand || 'unknown',
        card_number_masked: tokenData?.card?.last_four_digits || '****',
      } : {}),
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_POLLING_SUCCESS,
      status: 'success',
      provider: 'pagarme_v5',
      provider_id: linkData?.plan?.provider_id,
      trial_days: linkData?.plan?.trial_days,
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkPaymentFailed` em trackEvents.ts.
   */
  preparePaymentLinkPollingFailedForAnalytics(
    linkData,
    planData,
    subscriptionData,
    error
  ) {
    return {
      link_id: linkData.link,
      plan_id: planData?.id,
      plan_name: planData?.name,
      trial_days: planData?.trial_days,
      plan_amount: planData?.amount,
      subscription_id: subscriptionData.subscriptionId,
      subscription_status: subscriptionData.status,
      error_type: handleAxiosErrorMessage(error).error_type || 'polling_failed',
      error_message:
        handleAxiosErrorMessage(error).message ||
        'Falha no polling da assinatura',
      error_code: handleAxiosErrorMessage(error).status || 'unknown',
      error_data: handleAxiosErrorMessage(error).data,
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_POLLING_FAILED,
      status: 'failed',
      provider: 'pagarme_v5',
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkSubmissionFailed` em trackEvents.ts.
   */
  preparePaymentSubmissionFailedForAnalytics(
    linkData,
    planData,
    subscriptionData,
    error
  ) {
    return {
      link_id: linkData.link,
      allowed_payment_methods: linkData.allowedPaymentMethods,
      plan_id: planData?.id,
      plan_name: planData?.name,
      trial_days: planData?.trial_days,
      plan_amount: planData?.amount,
      subscription_id: subscriptionData.subscriptionId,
      subscription_status: subscriptionData.status,
      error_type: handleAxiosErrorMessage(error).error_type || 'polling_failed',
      error_message:
        handleAxiosErrorMessage(error).message ||
        'Falha no polling da assinatura',
      error_code: handleAxiosErrorMessage(error).status || 'unknown',
      error_data: handleAxiosErrorMessage(error).data,
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_SUBMISSION_FAILED,
      status: 'failed',
      provider: 'pagarme_v5',
    };
  }

  /**
   * Monta propriedades para `trackPaymentLinkPollingTimeout` em trackEvents.ts.
   */
  preparePaymentLinkPollingTimeoutForAnalytics(
    linkData,
    planData,
    subscriptionId,
    elapsedTime
  ) {
    return {
      link_id: linkData.link,
      allowed_payment_methods: linkData.allowedPaymentMethods,
      plan_id: planData?.id,
      plan_name: planData?.name,
      plan_amount: planData?.amount,
      subscription_id: subscriptionId,
      elapsed_time_seconds: elapsedTime,
      context: 'payment_link',
      timestamp: new Date().toISOString(),
      event_type: this.constructor.EVENT_PAYMENT_LINK_POLLING_TIMEOUT,
      status: 'timeout',
    };
  }
}

export default new PaymentLinkTrackingService();
